import { useModal } from "@/features/modal/ModalStackManager";
import { Calendar, CalendarDate } from "@/features/ui/Calendar";
import { Machine, MachineSearchBox } from "@/features/ui/PMS/machineSearchBox";
import { Button, Flex, Paper, Table, Text } from "@mantine/core";
import { IconCalendar, IconReportSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo, useState } from "react";

// dayjs 플러그인 확장
dayjs.extend(isBetween);

// 알람 데이터 타입
type AlarmData = {
  NO: number;
  발생시간: string;
  설비명: string;
  발생알람: string;
};

// 더미 데이터 생성 함수
const generateDummyData = (): AlarmData[] => {
  const alarms = ["절삭유 부족", "온도 이상", "압력 이상", "베어링 소음"];
  const machines = ["CNC 1호기", "CNC 2호기", "CNC 3호기"];
  const today = dayjs();
  const start = dayjs("2025-06-01");
  const end = dayjs("2025-07-10");

  const data: AlarmData[] = [];
  let current = start;
  let count = 1;

  while (current.isBefore(today.add(1, "day")) && current.isBefore(end)) {
    data.push({
      NO: count++,
      발생시간: current.format("YYYY-MM-DD 07:33:10"),
      설비명: machines[Math.floor(Math.random() * machines.length)],
      발생알람: alarms[Math.floor(Math.random() * alarms.length)],
    });
    current = current.add(Math.floor(Math.random() * 4) + 2, "day"); // 2~5일 간격
  }

  return data;
};

export default function CmsManagementPage() {
  const { openModal, closeModal } = useModal();
  const [dates, setDates] = useState<CalendarDate>([null, null]);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [allData] = useState<AlarmData[]>(generateDummyData());

  const handleSelectMachine = (machine: Machine | null) => {
    if (machine) {
      setSelectedMachine(machine);
      closeModal("");
    }
  };

  const filteredData = useMemo(() => {
    if (!dates[0] || !dates[1] || !selectedMachine) return [];

    const from = dayjs(dates[0]);
    const to = dayjs(dates[1]);
    const today = dayjs();

    return allData.filter((item) => {
      const date = dayjs(item.발생시간);
      return (
        item.설비명 === selectedMachine.name &&
        date.isBetween(from, to, "day", "[]") &&
        date.year() === 2025 &&
        (date.month() === 5 || date.month() === 6) &&
        date.isBefore(today.add(1, "day"))
      );
    });
  }, [dates, allData, selectedMachine]);

  return (
    <Flex direction="column" p="md" gap="md">
      <Flex justify="space-between" mb="md">
        <Flex direction="column">
          <Text size="xl">CMS 설비작업일보</Text>
          {selectedMachine && (
            <Text size="sm" color="dimmed">
              선택된 설비: {selectedMachine.name}
            </Text>
          )}
        </Flex>

        <Flex gap="md">
          <Button
            leftIcon={<IconCalendar />}
            color="lime"
            onClick={() => {
              openModal(
                <Calendar dates={[dates[0], dates[1]]} type="range" />,
                null,
                "날짜 선택",
              ).then((selectedDates) => {
                if (selectedDates) {
                  setDates(selectedDates);
                }
              });
            }}
          >
            {dates[0] && dates[1]
              ? `${dayjs(dates[0]).format("YYYY-MM-DD")} ~ ${dayjs(dates[1]).format("YYYY-MM-DD")}`
              : "날짜 선택"}
          </Button>
          <Button
            onClick={() => {
              openModal(
                <MachineSearchBox type="CNC" onSelect={handleSelectMachine} />,
                null,
                "기계 검색",
              );
            }}
            leftIcon={<IconReportSearch />}
          >
            설비 선택
          </Button>
        </Flex>
      </Flex>

      {/* 데이터 테이블 */}
      <Paper withBorder p="sm">
        <Table striped highlightOnHover>
          <thead>
            <tr>
              <th>NO</th>
              <th>발생시간</th>
              <th>설비명</th>
              <th>발생알람</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((row, index) => (
                <tr key={`${row.발생시간}-${row.설비명}`}>
                  <td>{index + 1}</td> {/* ✅ 재정렬된 NO */}
                  <td>{row.발생시간}</td>
                  <td>{row.설비명}</td>
                  <td>{row.발생알람}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} style={{ textAlign: "center" }}>
                  {selectedMachine ? "데이터가 없습니다." : "설비를 선택해주세요."}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Paper>
    </Flex>
  );
}
